import React, { useEffect, useState } from "react";

// const API_BASE_URL = "http://localhost:8000";
// API 통합관리
import { API_BASE_URL } from '../config';

interface AuthUser {
  id?: number;
  name?: string;
  email?: string;
  username?: string;
  phone?: string;
  team_id?: number;
  team_name?: string;
  grade?: string;
}

interface SupportAuthProps {
  onLoginSuccess?: (user?: AuthUser) => void;
}

const SupportAuth: React.FC<SupportAuthProps> = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState<"login" | "signup">("login");

  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const [rememberId, setRememberId] = useState(false);
  const [rememberPw, setRememberPw] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);

  const [signupForm, setSignupForm] = useState({
    name: "",
    phone: "",
    email: "",
    username: "",
    password: "",
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupSuccess, setSignupSuccess] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  useEffect(() => {
    const savedId = localStorage.getItem("ieg_saved_username") || "";
    const savedPw = localStorage.getItem("ieg_saved_password") || "";
    const savedAutoLogin = localStorage.getItem("ieg_auto_login") === "true";

    if (savedId) {
      setLoginForm((prev) => ({ ...prev, username: savedId }));
      setRememberId(true);
    }
    if (savedPw) {
      setLoginForm((prev) => ({ ...prev, password: savedPw }));
      setRememberPw(true);
    }
    if (savedAutoLogin) setAutoLogin(true);
  }, []);

  const handleLogin = async () => {
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setLoginError("아이디와 비밀번호를 입력해주세요.");
      return;
    }

    setLoginError("");
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });

      if (!response.ok) throw new Error();

      const data = await response.json().catch(() => ({}));

      if (rememberId)
        localStorage.setItem("ieg_saved_username", loginForm.username);
      else localStorage.removeItem("ieg_saved_username");

      if (rememberPw)
        localStorage.setItem("ieg_saved_password", loginForm.password);
      else localStorage.removeItem("ieg_saved_password");

      if (autoLogin) localStorage.setItem("ieg_auto_login", "true");
      else localStorage.removeItem("ieg_auto_login");

      if (onLoginSuccess) onLoginSuccess(data?.user);
    } catch {
      setLoginError("로그인에 실패했습니다. 아이디/비밀번호를 확인해주세요.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async () => {
    if (
      !signupForm.name.trim() ||
      !signupForm.email.trim() ||
      !signupForm.username.trim() ||
      !signupForm.password.trim()
    ) {
      setSignupSuccess("");
      setSignupError("이름, 이메일, 아이디, 비밀번호는 필수입니다.");
      return;
    }

    setSignupError("");
    setSignupSuccess("");
    setSignupLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signupForm),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || "회원가입 처리에 실패했습니다.");
      }

      setSignupSuccess("회원가입이 완료되었습니다. 로그인해주세요.");
      setSignupForm({
        name: "",
        phone: "",
        email: "",
        username: "",
        password: "",
      });
      setLoginForm((prev) => ({ ...prev, username: signupForm.username }));
      setMode("login");
    } catch (error: any) {
      setSignupError(error?.message || "회원가입 처리에 실패했습니다.");
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-8">
      <div className="mx-auto w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
        <section className="p-8 md:p-10 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                {mode === "login" ? "로그인" : "회원가입"}
              </h3>
              <p className="text-sm text-slate-500">
                {mode === "login"
                  ? "계정 정보를 입력해 시스템에 접속하세요."
                  : "신규 계정을 생성하고 CRM 시스템에 등록하세요."}
              </p>
            </div>

            <div className="grid grid-cols-2 rounded-xl p-1 bg-slate-100 border border-slate-200">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`py-2.5 rounded-lg text-sm font-black transition ${
                  mode === "login"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                로그인
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                className={`py-2.5 rounded-lg text-sm font-black transition ${
                  mode === "signup"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                회원가입
              </button>
            </div>
          </div>

          {mode === "login" ? (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  아이디
                </label>
                <input
                  type="text"
                  placeholder="아이디를 입력하세요"
                  className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  value={loginForm.username}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  onChange={(e) =>
                    setLoginForm({ ...loginForm, username: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    placeholder="비밀번호를 입력하세요"
                    className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition pr-12"
                    value={loginForm.password}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    onChange={(e) =>
                      setLoginForm({ ...loginForm, password: e.target.value })
                    }
                  />

                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                  >
                    {showLoginPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        viewBox="0 0 24 24"
                      >
                        <path d="M3 3l18 18M10.5 10.5a3 3 0 004.2 4.2M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        viewBox="0 0 24 24"
                      >
                        <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-600">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberId}
                    onChange={(e) => setRememberId(e.target.checked)}
                    className="peer hidden"
                  />
                  <div className="w-4 h-4 rounded border border-slate-300 peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition">
                    {rememberId && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  아이디 저장
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberPw}
                    onChange={(e) => setRememberPw(e.target.checked)}
                    className="peer hidden"
                  />
                  <div className="w-4 h-4 rounded border border-slate-300 peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition">
                    {rememberPw && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  비밀번호 저장
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoLogin}
                    onChange={(e) => setAutoLogin(e.target.checked)}
                    className="peer hidden"
                  />
                  <div className="w-4 h-4 rounded border border-slate-300 peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition">
                    {autoLogin && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  자동 로그인
                </label>
              </div>

              {loginError && (
                <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {loginError}
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={loginLoading}
                className="w-full py-3 rounded-xl font-black text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] transition disabled:opacity-50 disabled:hover:bg-blue-600"
              >
                {loginLoading ? "처리 중..." : "로그인"}
              </button>

              <div className="pt-1 text-center text-xs text-slate-500">
                <button className="hover:text-slate-700 hover:underline">
                  아이디 찾기
                </button>
                <span className="px-2 text-slate-300">|</span>
                <button className="hover:text-slate-700 hover:underline">
                  비밀번호 재설정
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  이름
                </label>
                <input
                  type="text"
                  placeholder="이름을 입력하세요"
                  className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  value={signupForm.name}
                  onChange={(e) =>
                    setSignupForm({ ...signupForm, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  이메일
                </label>
                <input
                  type="email"
                  placeholder="이메일을 입력하세요"
                  className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  value={signupForm.email}
                  onChange={(e) =>
                    setSignupForm({ ...signupForm, email: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  연락처(선택)
                </label>
                <input
                  type="text"
                  placeholder="연락처를 입력하세요"
                  className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  value={signupForm.phone}
                  onChange={(e) =>
                    setSignupForm({ ...signupForm, phone: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  아이디
                </label>
                <input
                  type="text"
                  placeholder="아이디를 입력하세요"
                  className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  value={signupForm.username}
                  onChange={(e) =>
                    setSignupForm({ ...signupForm, username: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">
                  비밀번호
                </label>
                <div className="relative">
                  <input
                    type={showSignupPassword ? "text" : "password"}
                    placeholder="비밀번호를 입력하세요"
                    className="w-full px-4 py-3 rounded-xl text-sm bg-slate-50 border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition pr-12"
                    value={signupForm.password}
                    onChange={(e) =>
                      setSignupForm({ ...signupForm, password: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                  >
                    {showSignupPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        viewBox="0 0 24 24"
                      >
                        <path d="M3 3l18 18M10.5 10.5a3 3 0 004.2 4.2M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        viewBox="0 0 24 24"
                      >
                        <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {signupError && (
                <div className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {signupError}
                </div>
              )}
              {signupSuccess && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                  {signupSuccess}
                </div>
              )}

              <button
                onClick={handleSignup}
                disabled={signupLoading}
                className="w-full py-3 rounded-xl font-black text-white bg-slate-900 hover:bg-slate-800 active:scale-[0.99] transition disabled:opacity-50 disabled:hover:bg-slate-900"
              >
                {signupLoading ? "처리 중..." : "회원가입"}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SupportAuth;
